import { ChangeDetectionStrategy, Component, effect, Input, output, viewChild, WritableSignal } from '@angular/core'
import { ModalComponent } from './modal.component'

/**
 * Abstract class for components ('.modal.ts') representing a modal.
 * It allows to handle / use the component as if it was a mos-modal.
 *
 * The component template must contain a main `mos-modal` element with a `#modal` reference.
 *
 * @example
 * ```html
 * @defer (when showCguUpdate()) {
 * <mos-cgu-update-modal [(opened)]="showCguUpdate"></mos-cgu-update-modal>
 * }
 * ```
 */
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: '',
})
export class AbstractModalComponent {
	protected modal = viewChild<ModalComponent>('modal')

	readonly mosOpen = output<void>()
	readonly mosOpened = output<void>()
	readonly mosClose = output<void>()
	readonly mosClosed = output<void>()
	readonly openedChange = output<boolean>()

	// Not converted to a signal input: accepts either a plain boolean or a WritableSignal (for
	// [(opened)]="mySignal" two-way bindings predating the model() API) and forwards to
	// ModalComponent.opened, which has the same dual-type setter and matching behavior quirks -
	// converting either in isolation risks changing how the two coordinate. Needs a deliberate,
	// coordinated pass across both components, not a mechanical one.
	@Input() set opened(opened: boolean | WritableSignal<boolean>) {
		const modal = this.modal()
		if (!modal) {
			return
		}

		modal.opened = opened
	}

	constructor() {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const onDialogAvailable = effect(() => {
			const modal = this.modal()
			if (!modal) {
				return
			}

			modal.mosOpen.subscribe(() => this.mosOpen.emit())
			modal.mosOpened.subscribe(() => this.mosOpened.emit())
			modal.mosClose.subscribe(() => this.mosClose.emit())
			modal.mosClosed.subscribe(() => this.mosClosed.emit())
			modal.openedChange.subscribe((opened) => this.openedChange.emit(opened))
		})
	}
}
